import { Button, Column } from '@tempmee/react';
import { OutlineMailIcon } from '@tempmee/react-icons';
import { type IJob } from '~/models';
import { InviteCountCaption } from '../InviteCountCaption';
import { EmbeddedCandidateList } from './EmbeddedCandidateList';
import { InviteSuccessPopup } from './InviteSuccessPopup';
import { useInterviewInvite } from './useInterviewInvite';

interface IInterviewInviteSectionProps {
  job: IJob;
}

/**
 * Left column of the redesigned Interview Details page (perm onsite): the inline
 * candidate list card, an Invite button that invites the selected candidates to
 * this interview, and a confirmation popup on success. Logic lives in
 * `useInterviewInvite`; already-invited candidates render as non-selectable "Sent"
 * rows.
 */
export const InterviewInviteSection = ({ job }: IInterviewInviteSectionProps) => {
  const {
    date,
    listKey,
    setSelectedIds,
    canInvite,
    isInviting,
    invitedCount,
    showSuccess,
    closeSuccess,
    invite,
  } = useInterviewInvite(job);

  return (
    <Column gap={6} data-cy="interview-invite-section" width="100%">
      <EmbeddedCandidateList
        key={listKey}
        postingId={job.postingId}
        interviewId={job.id}
        date={date}
        onSelectionChange={setSelectedIds}
      />

      <Column align="center" gap={2}>
        <Button
          startIcon={<OutlineMailIcon size={20} />}
          data-cy="interview-invite-button"
          size="small"
          disabled={!canInvite}
          loading={isInviting}
          onClick={invite}
        >
          Invite
        </Button>
        <InviteCountCaption count={invitedCount} />
      </Column>

      {showSuccess && (
        <InviteSuccessPopup
          dataCy="interview-invite-success"
          description="This interview has been successfully sent to your selected candidates."
          onClose={closeSuccess}
        />
      )}
    </Column>
  );
};
